import { FC } from 'react';

import { useTranslation } from 'react-i18next';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { useTheme } from 'styled-components';

import CustomGraphicTooltip from '@/components/RGraphicTooltip';
import useCurrency from '@/contexts/useCurrency';
import { Theme } from '@/theme/index';
import { ComparisonChartProps } from '@/types/report';
import { formatCompactNumber } from '@/utils';

const ComparisonChart: FC<ComparisonChartProps> = ({
  chartData,
  selectedMetric,
  closedDataKeys,
  toggleClosedDataKeys,
}) => {
  const theme = useTheme() as Theme;
  const { t } = useTranslation();
  const { currencySymbol } = useCurrency();
  const isGrossProfit = selectedMetric.includes('grossProfit');
  const unit = isGrossProfit ? currencySymbol : '%';

  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={chartData}>
        <CartesianGrid
          vertical={true}
          horizontal={true}
          strokeDasharray={'3 3'}
        />
        <XAxis
          dataKey="name"
          angle={-45}
          textAnchor="end"
          height={100}
          tick={{ fontSize: 12 }}
          tickFormatter={(value) => {
            if (value.length > 15) {
              return value.substring(0, 15) + '...';
            }
            return value;
          }}
          label={{
            value: t('components.simReport.comparisonChart.xAxisLabel'),
            position: 'bottom',
            offset: -15,
            style: { fontSize: 14 },
          }}
        />
        <ReferenceLine
          y={0}
          stroke="#dfdfdf"
          strokeWidth={1}
          strokeDasharray="3 3"
        />

        <YAxis
          domain={(() => {
            if (chartData.length === 0) return ['auto', 'auto'];

            const allValues = chartData.flatMap((item) => [
              item.beforeStorage,
              item.afterStorage,
              item.storage,
            ]);

            const minValue = Math.min(...allValues);
            const maxValue = Math.max(...allValues);
            const range = maxValue - minValue;
            const padding = range * 0.1;

            return [
              Math.floor(minValue - padding),
              Math.ceil(maxValue + padding),
            ];
          })()}
          ticks={(() => {
            if (chartData.length === 0) return [0];

            const allValues = chartData.flatMap((item) => [
              item.beforeStorage,
              item.afterStorage,
              item.storage,
            ]);

            const minValue = Math.min(...allValues);
            const maxValue = Math.max(...allValues);
            const range = maxValue - minValue;
            const padding = range * 0.1;

            const domainMin = Math.floor(minValue - padding);
            const domainMax = Math.ceil(maxValue + padding);

            const step = (domainMax - domainMin) / 6;
            const ticks = [];

            for (let i = 0; i <= 6; i++) {
              ticks.push(Math.round(domainMin + step * i));
            }

            if (!ticks.includes(0)) {
              ticks.push(0);
            }

            return ticks.sort((a, b) => a - b).slice(0, 8);
          })()}
          tickFormatter={
            isGrossProfit
              ? (value) => `${currencySymbol}${formatCompactNumber(value)}`
              : undefined
          }
          allowDecimals={false}
          label={{
            value: t('components.simReport.comparisonChart.yAxisLabel', {
              metric: selectedMetric.toUpperCase(),
              unit,
            }),
            angle: -90,
            position: 'insideLeft',
            offset: isGrossProfit ? 0 : 15,
            dy: 60,
            style: { fontSize: 14 },
          }}
        />
        <Tooltip content={<CustomGraphicTooltip />} />
        <Legend
          payload={[
            {
              value: t(
                'components.simReport.comparisonChart.legend.beforeStorage'
              ),
              id: 'beforeStorage',
              type: 'circle',
              color: theme.colors.greenSecondary,
            },
            {
              value: t(
                'components.simReport.comparisonChart.legend.afterStorage'
              ),
              id: 'afterStorage',
              type: 'circle',
              color: theme.colors.blueLightAccent,
            },
            {
              value: t('components.simReport.comparisonChart.legend.storage'),
              id: 'storage',
              type: 'circle',
              color: theme.colors.orangeSecondary,
            },
          ]}
          iconSize={10}
          onClick={({ id }) => id && toggleClosedDataKeys(id)}
        />
        {!closedDataKeys.includes('beforeStorage') && (
          <Bar
            dataKey="beforeStorage"
            fill={theme.colors.greenSecondary}
            name={t(
              'components.simReport.comparisonChart.barNames.beforeStorage',
              { metric: selectedMetric.toUpperCase() }
            )}
            unit={unit}
          />
        )}
        {!closedDataKeys.includes('afterStorage') && (
          <Bar
            dataKey="afterStorage"
            fill={theme.colors.blueLightAccent}
            name={t(
              'components.simReport.comparisonChart.barNames.afterStorage',
              { metric: selectedMetric.toUpperCase() }
            )}
            unit={unit}
          />
        )}
        {!closedDataKeys.includes('storage') && (
          <Bar
            dataKey="storage"
            fill={theme.colors.orangeSecondary}
            name={t('components.simReport.comparisonChart.barNames.storage', {
              metric: selectedMetric.toUpperCase(),
            })}
            unit={unit}
          />
        )}
      </BarChart>
    </ResponsiveContainer>
  );
};

export default ComparisonChart;
